/**
 * Display settings: which framebuffer the engine renders into.
 *
 * fallout2-ce implements the old Hi-Res patch natively, so there is no scaler
 * to configure — `[screen] resolution_x/y` in fallout2.cfg *is* the world
 * resolution, and a bigger one means more map on screen rather than bigger
 * pixels. The bundled engine can rebuild that framebuffer live.
 *
 * Two surfaces, one state: an inline select on the gate (chosen before START,
 * so no reload is needed) and an overlay reachable from the play chrome (mid
 * session, where the engine mailbox applies it). Both are driven from here.
 */

import {
  DISPLAY_MODE_SPECS,
} from '../config';
import { $ } from './dom';

export interface SettingsPanel {
  open(): void;
  close(): void;
  currentId(): string;
}

function optionsFor(select: HTMLSelectElement, current: string): void {
  select.replaceChildren();
  for (const spec of DISPLAY_MODE_SPECS) {
    const opt = document.createElement('option');
    opt.value = spec.id;
    opt.textContent = spec.name;
    select.appendChild(opt);
  }
  select.value = current;
}

/**
 * @param initialId  persisted mode id
 * @param onChange   persists the new id
 * @param onApply    asks the running engine to apply the selected mode
 */
export function mountSettings(
  initialId: string,
  initialTouchScale: number,
  onChange: (id: string) => Promise<void>,
  onApply: (id: string) => Promise<void>,
  onTouchScaleChange: (scale: number) => Promise<void>
): SettingsPanel {
  const overlay = $('#overlay-settings');
  const gateSelect = $<HTMLSelectElement>('#display-mode');
  const overlayChoices = [...overlay.querySelectorAll<HTMLButtonElement>('[data-display-mode]')];
  const reloadBtn = $<HTMLButtonElement>('#btn-settings-reload');
  const touchScaleSelect = $<HTMLSelectElement>('#touch-assist-scale');

  let current = DISPLAY_MODE_SPECS.some((s) => s.id === initialId) ? initialId : 'classic';
  let touchScale = [85, 100, 125, 150].includes(initialTouchScale) ? initialTouchScale : 100;

  const render = () => {
    optionsFor(gateSelect, current);
    overlayChoices.forEach((choice) => {
      choice.setAttribute('aria-pressed', String(choice.dataset.displayMode === current));
    });
    touchScaleSelect.value = String(touchScale);
  };

  const pick = async (id: string, live: boolean) => {
    current = id;
    await onChange(id);
    render();
    reloadBtn.hidden = !live;
  };

  gateSelect.addEventListener('change', () => void pick(gateSelect.value, false));
  overlayChoices.forEach((choice) => {
    choice.addEventListener('click', () => void pick(choice.dataset.displayMode ?? current, true));
  });
  touchScaleSelect.addEventListener('change', () => {
    touchScale = Number.parseInt(touchScaleSelect.value, 10) || 100;
    void onTouchScaleChange(touchScale);
  });
  reloadBtn.textContent = 'Apply';
  reloadBtn.addEventListener('click', () => {
    reloadBtn.disabled = true;
    reloadBtn.textContent = 'Applying…';
    void onApply(current)
      .then(() => {
        reloadBtn.hidden = true;
        overlay.classList.remove('open');
        // The Apply button owns focus while the overlay is open. Hand it back
        // to the game explicitly so physical keyboard shortcuts (notably I and
        // P) keep going to SDL after a live resize.
        $<HTMLCanvasElement>('#canvas').focus({ preventScroll: true });
      })
      .catch(() => {})
      .finally(() => {
        reloadBtn.disabled = false;
        reloadBtn.textContent = 'Apply';
      });
  });

  $('#btn-settings-close').addEventListener('click', () => overlay.classList.remove('open'));
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.classList.remove('open');
  });

  render();
  reloadBtn.hidden = true;

  return {
    open() {
      render();
      overlay.classList.add('open');
    },
    close() {
      overlay.classList.remove('open');
    },
    currentId: () => current,
  };
}
